import React from 'react';

import {
  ActivityTypeGroups, isPartnerReferralActivity, isPatientAssessmentFormActivity,
  isPatientMatchActivity,
  isPatientPsychiatryActivity,
  PatientActivity
} from '@uplift/api';
import { usePatientActivityQuery } from '@uplift/queries';

export const useFindInitialActivity = (
  {
    patientId,
    activityType,
    activityId,
    onFound
  }: {
    patientId: string;
    activityType: ActivityTypeGroups | undefined;
    activityId: string | undefined;
    onFound: (activity: PatientActivity) => void;
  }
) => {
  const enabled = Boolean(activityType && activityId);

  const patientActivityQuery = usePatientActivityQuery(patientId, {}, {
    enabled
  });

  const findInitialActivity = React.useCallback(async () => {
    let fetchResult = await patientActivityQuery.fetchNextPage();

    do {
      const { data, hasNextPage, fetchNextPage, isLoading, isFetching } = fetchResult;

      if (!enabled) {
        return null;
      }

      if (!data?.pages && isLoading) {
        return null;
      }

      const activity = data.pages.flatMap(x => x.data).find(activity => {
        if (activityType === 'assessment' && isPatientAssessmentFormActivity(activity)) {
          if (activity.submission.formSubmissionId === activityId || activity.submission.formId === activityId) {
            return activity;
          }
        } else if (activityType === 'psychiatrySession' && isPatientPsychiatryActivity(activity)) {
          if (activity.appointment.id === activityId) {
            return activity;
          }
        } else if (activityType === 'referral' && isPartnerReferralActivity(activity)) {
          if (activity.aboutUser.user.id === activityId) {
            return activity;
          }
        } else if (activityType === 'match' && isPatientMatchActivity(activity)) {
          if (activity.match.createdBy === activityId) {
            return activity;
          }
        }

        return null;
      });

      if (activity) {
        return activity;
      }

      // this shouldn't be necessary but let's be defensive
      if (!hasNextPage && !isFetching) {
        return null;
      }

      if (hasNextPage && !isFetching) {
        fetchResult = await fetchNextPage();
      }
    } while (fetchResult.hasNextPage && !fetchResult.isFetching);

    // nothing has been found
    return null;
  }, [ activityId, activityType, enabled ]);

  const findInitialActivityRef = React.useRef(findInitialActivity);
  const onFoundRef = React.useRef(onFound);

  findInitialActivityRef.current = findInitialActivity;
  onFoundRef.current = onFound;


  React.useEffect(() => {
    const find = async () => {
      const initialActivity = await findInitialActivityRef.current();

      if (initialActivity) {
        onFoundRef.current(initialActivity);
      }
    };

    find();
  }, [ activityType ]);
};
